import { useLingui } from '@lingui/react/macro';
import { useId, type ReactNode } from 'react';
import { useFormState } from 'react-hook-form';

import { readPath } from '@/shared/patterns';
import { cn } from '@/shared/ui/cn';

export interface FormSectionProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Número da seção (arquétipo Formulário, U39 §1: seções numeradas). */
  readonly number?: number;
  /** Colunas da grade a partir de 768 px (1 coluna no iPhone). */
  readonly columns?: 1 | 2 | 3;
  /** Campos da seção: a contagem de erros aparece no título (protótipo, iPhone). */
  readonly fields?: readonly string[];
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * Recipiente de um grupo de campos (06 §3.d): título, grade de 2–3 colunas na web e 1 no iPhone.
 * Os blocos com schema (`AddressFields`, `BankAccountFields`… da onda 1) entram como conteúdo.
 */
export function FormSection({ title, description, number, columns = 3, fields, children, className }: FormSectionProps) {
  const { t } = useLingui();
  const headingId = useId();
  const { errors } = useFormState();
  const errorCount = fields ? fields.filter((name) => readPath(errors, name) !== undefined).length : 0;
  return (
    <section aria-labelledby={headingId} data-slot="form-section" className={cn('rounded-card border border-border bg-card p-4 md:p-6', className)}>
      <header className="mb-4 flex items-start gap-3">
        {number !== undefined ? (
          <span aria-hidden className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-xs font-semibold text-foreground">
            {number}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 id={headingId} className="text-base font-semibold text-foreground md:text-lg">
            {title}
          </h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {errorCount > 0 ? (
          <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-err-wash px-1.5 text-xs font-semibold text-err">
            {errorCount}
            <span className="sr-only">{errorCount === 1 ? t`campo com erro` : t`campos com erro`}</span>
          </span>
        ) : null}
      </header>
      <div className={cn('grid gap-4', columns >= 2 && 'md:grid-cols-2', columns === 3 && 'lg:grid-cols-3')}>{children}</div>
    </section>
  );
}
